import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { apiFetch, callApi } from '../api';
import Modal from '../components/Modal';
import InfoTip from '../components/InfoTip';
import ScopePicker from '../components/ScopePicker';
import RevealKeyModal from '../components/RevealKeyModal';
import { useConfirm } from '../components/ConfirmDialog';
import {
  Badge, EmptyState, Field, Icon, PageHeader, Switch, TableSkeleton,
} from '../components/ui';

// ── External API admin ─────────────────────────────────────────────────────
// Cognito-gated CRUD over the api_keys table. Producers authenticate with
// X-Api-Key against /api/external/users/{external_id}/… — every key carries
// a scopes array which gates the underlying endpoints.
//
// (Written "…", not "/" followed by "*": the copy-standard tests strip block comments
// with a regex, and that pair inside a line comment opens one that swallows every
// component down to the next "*" + "/", hiding this page's useConfirm from them.)
//
// Single-pane layout (no tabs): page header + key table + modals.
// Create flow: header button → CreateKeyModal → POST returns `{key, row}`
// → RevealKeyModal shows the plain key exactly once.

// Short enough for a table cell. The full timestamp, seconds included, rides in
// the cell's tooltip. UTC throughout, so every operator reads the same clock.
const SHORT_DATE = {
  timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit', hour12: false,
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', SHORT_DATE) + ' UTC';
  } catch { return iso; }
}

function fullDate(iso) {
  if (!iso) return undefined;
  try {
    return new Date(iso).toLocaleString('en-GB', { timeZone: 'UTC', hour12: false }) + ' UTC';
  } catch { return iso; }
}

function isExpired(k) {
  return !!k.expires_at && new Date(k.expires_at).getTime() <= Date.now();
}

// Disabled wins over expired: a disabled key is refused whatever its date. An
// enabled key past `expires_at` is refused too (401 "API key expired"), which
// the enabled switch alone would not show.
function keyStatus(k) {
  if (!k.enabled) return { tone: 'neutral', label: 'Disabled' };
  if (isExpired(k)) return { tone: 'warn', label: 'Expired' };
  return { tone: 'ok', label: 'Active' };
}

// ── Scope badges ───────────────────────────────────────────────────────────
// The first two scope ids, then "+N" for the rest. The overflow is spelled out
// for screen readers and in the tooltip, so nothing is only a hover away.
const SCOPES_SHOWN = 2;

function ScopeBadges({ scopes, labelFor }) {
  const arr = Array.isArray(scopes) ? scopes : [];
  if (arr.length === 0) {
    return <Badge tone="danger" title="This key cannot read anything">No scopes</Badge>;
  }
  const rest = arr.slice(SCOPES_SHOWN);
  return (
    <div className="row-wrap gap-1">
      {arr.slice(0, SCOPES_SHOWN).map((s) => (
        <Badge key={s} tone="outline" mono title={labelFor(s)}>{s}</Badge>
      ))}
      {rest.length > 0 && (
        <Badge tone="neutral" title={rest.join(', ')}>
          +{rest.length}<span className="sr-only"> more: {rest.join(', ')}</span>
        </Badge>
      )}
    </div>
  );
}

// ── Create-Key Modal (kept private to this page) ───────────────────────────
// One pane. It was two tabs (Details / Scopes) so the dialog fitted a laptop
// screen with several scope groups expanded; the picker now caps its own height
// and scrolls inside it, so both halves fit at once. That also means neither
// required field sits on a tab the operator is not looking at while Create
// stays disabled. Both fields participate in the submit guard.
function CreateKeyModal({ catalog, onSubmit, onCancel }) {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState([]);
  const [expiresAt, setExpiresAt] = useState('');
  const [busy, setBusy] = useState(false);

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && scopes.length > 0 && !busy;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const payload = {
        name: trimmedName,
        scopes,
      };
      if (expiresAt) {
        // datetime-local gives "YYYY-MM-DDTHH:mm" (local). Convert to ISO
        // (UTC) so the backend stores a tz-aware timestamp.
        const d = new Date(expiresAt);
        if (!isNaN(d.getTime())) payload.expires_at = d.toISOString();
      }
      await onSubmit(payload);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={busy ? undefined : onCancel}
      title="New API key"
      maxWidth={640}
    >
      <div className="stack gap-4">
        <div className="form-row">
          <Field
            label="Name"
            htmlFor="api-key-name"
            required
            className="mb-0"
            hint="A label for this page, never part of the key. Up to 64 letters, digits, spaces, - or _."
          >
            <input
              id="api-key-name"
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ecommerce-prod-readonly"
              spellCheck={false}
              autoComplete="off"
              autoFocus
            />
          </Field>

          <Field
            label="Expires at"
            htmlFor="api-key-expires"
            className="mb-0"
            extra={<span className="text-faint fw-400">(optional)</span>}
            hint={(
              <>
                In your local time. Leave empty for a key that never expires. After it,
                every call gets 401 with detail <code>"API key expired"</code>.
              </>
            )}
          >
            <input
              id="api-key-expires"
              type="datetime-local"
              className="form-input"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </Field>
        </div>

        <div>
          <div className="row between mb-2">
            <span id="api-key-scopes-label" className="form-label mb-0">
              Scopes<span className="required" aria-hidden="true">*</span>
            </span>
            <Badge tone={scopes.length === 0 ? 'danger' : 'accent'}>{scopes.length} selected</Badge>
          </div>
          <ScopePicker
            value={scopes}
            onChange={setScopes}
            catalog={catalog}
            labelledBy="api-key-scopes-label"
            maxHeight="min(300px, 36vh)"
          />
          <p className="form-hint">
            Pick at least one permission. The key is rejected with 403 on any endpoint it
            doesn&apos;t have a scope for.
          </p>
        </div>
      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary" disabled={!canSubmit} onClick={handleSubmit}>
          {busy ? <><span className="spinner-sm" /> Creating…</> : 'Create key'}
        </button>
      </div>
    </Modal>
  );
}

// ── Edit-Scopes Modal ──────────────────────────────────────────────────────
// Edits the scopes of an EXISTING key in place (PATCH /api/api-keys/{id}). No
// new key is issued and the secret is never re-shown — only the scopes array
// changes. Pre-populated with the key's current scopes via the same catalog-
// driven ScopePicker the create flow uses.
function EditScopesModal({ apiKey, catalog, onSubmit, onCancel }) {
  const [scopes, setScopes] = useState(Array.isArray(apiKey.scopes) ? apiKey.scopes : []);
  const [busy, setBusy] = useState(false);
  const canSubmit = scopes.length > 0 && !busy;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await onSubmit(scopes);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={busy ? undefined : onCancel}
      title={`Edit scopes for ${apiKey.name}`}
      description="Changes apply to this key immediately. No new key is issued and the secret is not shown again."
      maxWidth={640}
    >
      <div className="row between mb-2">
        <span id="api-key-edit-scopes-label" className="form-label mb-0">Scopes</span>
        <Badge tone={scopes.length === 0 ? 'danger' : 'accent'}>{scopes.length} selected</Badge>
      </div>
      <ScopePicker
        value={scopes}
        onChange={setScopes}
        catalog={catalog}
        labelledBy="api-key-edit-scopes-label"
        maxHeight="min(360px, 45vh)"
      />
      <p className="form-hint">Pick at least one permission.</p>

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary" disabled={!canSubmit} onClick={handleSubmit}>
          {busy ? <><span className="spinner-sm" /> Saving…</> : 'Save scopes'}
        </button>
      </div>
    </Modal>
  );
}

// ── Page root ──────────────────────────────────────────────────────────────
export default function Api() {
  const [keys, setKeys] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [revealedKey, setRevealedKey] = useState(null);
  const [actionBusy, setActionBusy] = useState({});
  const [confirm, confirmDialog] = useConfirm();

  const setBusy = (id, action) =>
    setActionBusy((p) => ({ ...p, [id]: action }));

  const fetchKeys = useCallback(async () => {
    try {
      const resp = await apiFetch('/api/api-keys');
      if (resp.ok) {
        const data = await resp.json();
        setKeys(Array.isArray(data) ? data : (data.keys || []));
      } else {
        toast.error('Failed to load API keys');
      }
    } catch {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCatalog = useCallback(async () => {
    try {
      const resp = await apiFetch('/api/api-keys/scopes');
      if (resp.ok) {
        const data = await resp.json();
        setCatalog(Array.isArray(data) ? data : (data.scopes || []));
      }
    } catch {
      // Non-fatal — the ScopePicker will show "Loading scopes…" until
      // the operator dismisses the modal.
    }
  }, []);

  useEffect(() => { fetchKeys(); fetchCatalog(); }, [fetchKeys, fetchCatalog]);

  // Scope id → the catalogue's label, for the tooltip on each scope badge.
  const scopeLabels = useMemo(() => {
    const out = {};
    catalog.forEach((group) => (group.permissions || []).forEach((p) => { out[p.id] = p.label; }));
    return out;
  }, [catalog]);
  const labelFor = (id) => scopeLabels[id];

  const handleCreate = async (payload) => {
    const data = await callApi('/api/api-keys', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }, { successMsg: 'API key created', errorMsg: 'Create failed' });
    if (data && data.key) {
      setCreating(false);
      setRevealedKey(data.key);
      fetchKeys();
    } else if (data) {
      // Backend returned 200 but no `key` field — surface the row anyway.
      setCreating(false);
      fetchKeys();
    }
  };

  const handleEditScopes = async (scopes) => {
    if (!editing) return;
    const data = await callApi(`/api/api-keys/${editing.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ scopes }),
    }, { successMsg: 'Scopes updated', errorMsg: 'Update failed' });
    if (data) {
      setEditing(null);
      fetchKeys();
    }
  };

  const handleToggle = async (row) => {
    setBusy(row.id, row.enabled ? 'disable' : 'enable');
    const data = await callApi(`/api/api-keys/${row.id}/toggle`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: !row.enabled }),
    }, { successMsg: `Key ${row.enabled ? 'disabled' : 'enabled'}`, errorMsg: 'Toggle failed' });
    setBusy(row.id, null);
    if (data) fetchKeys();
  };

  const handleDelete = async (row) => {
    if (!await confirm({
      title: `Delete API key ${row.name}?`,
      body: (
        <p>
          It stops working immediately and cannot be recovered, so anything using it
          gets a <strong>401</strong> on its next call.
        </p>
      ),
      confirmLabel: 'Delete key',
    })) return;
    setBusy(row.id, 'delete');
    const data = await callApi(`/api/api-keys/${row.id}`, { method: 'DELETE' },
      { successMsg: 'API key deleted', errorMsg: 'Delete failed' });
    setBusy(row.id, null);
    if (data) fetchKeys();
  };

  // Render the prefix with a fixed-width bullet suffix so columns line up:
  //   apk_a1b2c3d4••••
  const renderPrefix = (prefix) => {
    if (!prefix) return <span className="text-faint">—</span>;
    return (
      <code className="nowrap">
        {prefix}<span className="text-faint">••••</span>
      </code>
    );
  };

  const openCreate = () => setCreating(true);

  return (
    <div>
      {/* Title, tip, lede and the primary action all lifted out of the card, which was
          otherwise announcing "API keys" twice on a single-card page. */}
      <PageHeader
        description={(
          <>
            Keys an external producer uses to read and manage its users over{' '}
            <code>/api/external/*</code>. Each key is limited to the scopes you grant it.
          </>
        )}
        actions={(
          /* "New key", like "New user" and "New tier". The empty state carries its
             own "Create the first key" button instead of naming this one, which is
             how the two drifted apart before ("New key" here, "+ Create key" there). */
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" /> New key
          </button>
        )}
      >
        <h1 className="page-title">
          API keys
          {/* Was seven paragraphs and a list, 1590 characters, which is longer than the
              table it covers and so read by nobody. Cut to the three facts that change
              what an operator DOES. The rest is recorded here rather than deleted:

                - Sent as an `X-Api-Key` header. Scopes cover reads (token, bot handle,
                  consumption, state, profile, schedules, tasks, tools, connected services,
                  assistant instructions) and writes (create, edit, start, stop, reset
                  quota, tier change, edit instructions), each granted separately.
                - `tools` and `connected services` are easy to confuse: the first reports
                  our own switches from our database, the second calls out to RossGate and
                  is therefore the one read that can return 502. A 502 means UNKNOWN, never
                  disconnected.
                - The table shows the key prefix, which is safe to paste into a ticket.
                - Every call is recorded on the key's audit trail, refusals included.
                - A key can be restricted to specific users, which makes every other user a
                  404 to it. Set through the API, not from this page. */}
          <InfoTip label="About API keys" width={360}>
            <p>
              Shown in full once, at creation. Only a SHA-256 hash is stored, so a lost key
              is replaced, never recovered.
            </p>
            <p>
              <strong>401</strong> means unknown, disabled or expired.{' '}
              <strong>403</strong> means valid but missing that scope.
            </p>
            <p>
              Write access to assistant instructions is the heaviest grant: editing{' '}
              <code>SOUL.md</code> or <code>AGENTS.md</code> stops the platform maintaining
              that file for the user.
            </p>
          </InfoTip>
          {!loading && <Badge tone="neutral" size="lg">{keys.length}</Badge>}
        </h1>
      </PageHeader>

      <div className="card card-flush">
        {loading ? (
          <TableSkeleton rows={3} cols={7} label="Loading API keys" />
        ) : keys.length === 0 ? (
          <EmptyState
            icon="key"
            title="No API keys yet"
            action={<button type="button" className="btn btn-primary" onClick={openCreate}>Create the first key</button>}
          >
            A key lets an external producer call the external API for its users, limited to
            the scopes you grant it.
          </EmptyState>
        ) : (
          <div className="table-container">
            <table className="table table-stack">
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Prefix</th>
                  <th>Scopes</th>
                  <th>Status</th>
                  <th>Last used</th>
                  <th>Enabled</th>
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => {
                  const busy = actionBusy[k.id];
                  const status = keyStatus(k);
                  const switchId = `api-key-enabled-${k.id}`;
                  // A restricted key answers 404 for every user outside its list, which
                  // reads as "user not found" from the producer's side. Set through the
                  // API only, so this is the one place it is visible.
                  const restricted = Array.isArray(k.allowed_external_ids)
                    ? k.allowed_external_ids.length : 0;
                  const expiry = !k.expires_at ? null
                    : status.label === 'Expired' ? formatDate(k.expires_at)
                    : `${isExpired(k) ? 'Expired' : 'Expires'} ${formatDate(k.expires_at)}`;
                  return (
                    <tr key={k.id}>
                      <td data-label="Key">
                        <div style={{ maxWidth: 240, minWidth: 0 }}>
                          <span className="cell-primary">{k.name}</span>
                          <span className="cell-sub" title={fullDate(k.created_at)}>
                            Created {formatDate(k.created_at)}
                          </span>
                          {restricted > 0 && (
                            <span className="cell-sub">
                              Restricted to {restricted} user{restricted === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td data-label="Prefix">{renderPrefix(k.key_prefix)}</td>
                      <td data-label="Scopes" className="wrap">
                        <ScopeBadges scopes={k.scopes} labelFor={labelFor} />
                      </td>
                      <td data-label="Status">
                        <div className="stack gap-1" style={{ alignItems: 'flex-start' }}>
                          <Badge tone={status.tone} dot>{status.label}</Badge>
                          {expiry && (
                            <span className="text-xs text-muted nowrap" title={fullDate(k.expires_at)}>
                              {expiry}
                            </span>
                          )}
                        </div>
                      </td>
                      <td data-label="Last used" className="nowrap" title={fullDate(k.last_used_at)}>
                        {k.last_used_at
                          ? <span className="num">{formatDate(k.last_used_at)}</span>
                          : <span className="text-faint">Never</span>}
                      </td>
                      <td data-label="Enabled">
                        <div className="row gap-2">
                          <label htmlFor={switchId} className="sr-only">Enable key {k.name}</label>
                          <Switch
                            id={switchId}
                            checked={k.enabled}
                            disabled={!!busy}
                            onChange={() => handleToggle(k)}
                          />
                          {(busy === 'enable' || busy === 'disable') && (
                            <span className="spinner-sm text-muted" aria-hidden="true" />
                          )}
                        </div>
                      </td>
                      <td data-label="Actions">
                        <div className="row gap-1 row-end">
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm btn-icon"
                            disabled={!!busy}
                            onClick={() => setEditing(k)}
                            aria-label={`Edit scopes for ${k.name}`}
                            title="Edit scopes"
                          >
                            <Icon name="edit" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger-soft btn-sm btn-icon"
                            disabled={!!busy}
                            onClick={() => handleDelete(k)}
                            aria-label={`Delete key ${k.name}`}
                            title="Delete key"
                          >
                            {busy === 'delete' ? <span className="spinner-sm" aria-hidden="true" /> : <Icon name="trash" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating && (
        <CreateKeyModal
          catalog={catalog}
          onSubmit={handleCreate}
          onCancel={() => setCreating(false)}
        />
      )}

      {editing && (
        <EditScopesModal
          apiKey={editing}
          catalog={catalog}
          onSubmit={handleEditScopes}
          onCancel={() => setEditing(null)}
        />
      )}

      <RevealKeyModal
        apiKey={revealedKey}
        onClose={() => setRevealedKey(null)}
      />

      {confirmDialog}
    </div>
  );
}
